/**
 * @fileoverview AdminPageBanner — Reusable banner component for admin pages
 *
 * Renders a full-width hero banner with page title, subtitle, stat count,
 * optional toggle switch, and optional add button. Supports an optional
 * children prop for custom content (e.g., project type breakdown on Dashboard).
 *
 * Used on: Dashboard, Projects, Clients, Vendors
 *
 * Layout:
 * - Desktop: Title/subtitle left, stat + add button + toggle right (single row)
 * - Mobile: Title + count left, toggle + FAB right (compact stacked)
 *
 * @component
 * @example
 * // Standard page (Projects, Clients, Vendors)
 * <AdminPageBanner
 *   title="Projects"
 *   subtitle="Manage all active and upcoming projects"
 *   count={12}
 *   countLabel="Active Projects"
 *   addButton={{ label: "New Project", onClick: handleAdd }}
 *   toggle={{ label: "Testing Mode", mobileLabel: "Test", checked: false, onChange: setChecked }}
 * />
 *
 * @example
 * // Dashboard with custom children (project type buttons)
 * <AdminPageBanner
 *   title="Dashboard"
 *   subtitle="Here's what's happening across your projects today."
 *   greeting={`Welcome back, ${userName}`}
 *   count={12}
 *   countLabel="Active Projects"
 *   loading={statsLoading}
 * >
 *   <div className="admin-dash-projects-breakdown">...</div>
 * </AdminPageBanner>
 *
 * @requires react
 * @requires react-icons/fa
 * @version 1.0.0
 */

import React from "react";
import { FaPlus, FaSpinner } from "react-icons/fa";
import "./AdminPageBanner.css";

/**
 * AdminPageBanner Component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (e.g., "Projects", "Dashboard")
 * @param {string} [props.subtitle] - Descriptive subtitle below title
 * @param {string} [props.greeting] - Optional greeting line (Dashboard only)
 * @param {number} [props.count] - Stat number to display
 * @param {string} [props.countLabel] - Label below the stat number (e.g., "Active Projects")
 * @param {boolean} [props.loading=false] - Show loading spinner instead of stat/actions
 * @param {Object} [props.addButton] - Add button configuration
 * @param {string} props.addButton.label - Button text for desktop (e.g., "New Project")
 * @param {Function} props.addButton.onClick - Click handler
 * @param {Object} [props.toggle] - Toggle switch configuration
 * @param {string} props.toggle.label - Toggle label for desktop
 * @param {string} [props.toggle.mobileLabel] - Abbreviated label for mobile
 * @param {boolean} props.toggle.checked - Toggle state
 * @param {Function} props.toggle.onChange - Change handler (receives boolean)
 * @param {React.ReactNode} [props.children] - Custom content (e.g., project type breakdown)
 * @returns {JSX.Element} Banner component
 */
const AdminPageBanner = ({
  title,
  subtitle,
  greeting,
  count,
  countLabel,
  loading = false,
  addButton,
  toggle,
  children,
}) => {
  return (
    <div className="apb-banner">
      {/* Left side — Title & subtitle */}
      <div className="apb-banner-left">
        <h1 className="apb-banner-title">
          {title}
          {addButton && (
            <button
              className="apb-banner-add-btn"
              onClick={addButton.onClick}
            >
              <FaPlus /> <span>{addButton.label}</span>
            </button>
          )}
        </h1>
        {greeting && <span className="apb-banner-greeting">{greeting}</span>}
        {subtitle && <p className="apb-banner-subtitle">{subtitle}</p>}

        {/* Mobile: inline count below title */}
        {count != null && !loading && (
          <span className="apb-banner-count-mobile">
            {count} {countLabel?.toLowerCase() || ""}
          </span>
        )}
      </div>

      {/* Right side — Stat, add button, toggle, children */}
      <div className="apb-banner-right">
        {loading ? (
          <div className="apb-banner-loading">
            <FaSpinner className="apb-banner-spinner" />
            <span>Loading...</span>
          </div>
        ) : (
          <>
            {/* Stat count (desktop) */}
            {count != null && (
              <div className="apb-banner-stat">
                <span className="apb-banner-number">{count}</span>
                {countLabel && (
                  <span className="apb-banner-label">{countLabel}</span>
                )}
              </div>
            )}

            {/* Custom children (e.g., project type breakdown) */}
            {children}

            {/* Add button (desktop) */}
            {addButton && (
              <button
                className="apb-banner-add-btn"
                onClick={addButton.onClick}
              >
                <FaPlus />
                <span>{addButton.label}</span>
              </button>
            )}

            {/* Toggle switch (desktop) */}
            {toggle && (
              <div className="apb-banner-toggle">
                <label className="apb-banner-toggle-label">
                  <span className="apb-banner-toggle-text">{toggle.label}</span>
                  <div className="apb-banner-toggle-switch">
                    <input
                      type="checkbox"
                      checked={toggle.checked}
                      onChange={(e) => toggle.onChange(e.target.checked)}
                    />
                    <span className="apb-banner-toggle-slider"></span>
                  </div>
                </label>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile actions row — toggle + FAB */}
      {!loading && toggle && (
        <div className="apb-banner-mobile-actions">
          {toggle && (
            <div className="apb-banner-toggle-mobile">
              <label className="apb-banner-toggle-label">
                <span className="apb-banner-toggle-text-mobile">
                  {toggle.mobileLabel || toggle.label}
                </span>
                <div className="apb-banner-toggle-switch">
                  <input
                    type="checkbox"
                    checked={toggle.checked}
                    onChange={(e) => toggle.onChange(e.target.checked)}
                  />
                  <span className="apb-banner-toggle-slider"></span>
                </div>
              </label>
            </div>
          )}
        </div>
      )}
      {/* Mobile children row — full width, visible only on mobile */}
      {!loading && children && (
        <div className="apb-banner-children-mobile">{children}</div>
      )}
    </div>
  );
};

export default AdminPageBanner;
